import {
    ActivityCategory,
    CalendarDate,
    CalendarDateTime,
    ReferenceContactId,
} from "@sp-crm/core";
import { ActivityCategoryIcon } from "components/tasks/activity-chip";
import { CalendarDateTimeInput } from "components/ui/calendar-date-time";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { SpMultiSelect, SpMultiSelectOption } from "components/ui/sp-multi-select";
import { TextArea } from "components/ui/textarea";
import {
    attendeeLabel,
    attendeePlaceholder,
} from "components/visits/attendee-candidates";
import React from "react";

export interface LogActivitySubmission {
    note: string | null;
    happenedAt: Date;
}

export interface AttendeePickerProps {
    options: SpMultiSelectOption<ReferenceContactId>[];
    value: ReferenceContactId[];
    onChange: (ids: ReferenceContactId[]) => void;
}

export const resolveHappenedAt = (when: CalendarDateTime | null): Date => {
    if (!when) {
        return new Date();
    }
    if (when.hasTime) {
        return when.date;
    }
    if (when.dateString === CalendarDate.todayNoTime.toString()) {
        return new Date();
    }
    return when.date;
};

interface LogActivityFormProps {
    typeLabel: string;
    category: ActivityCategory;
    submitLabel: string;
    isSubmitting: boolean;
    attendeePicker: AttendeePickerProps | null;
    onSubmit: (submission: LogActivitySubmission) => void;
    onCancel: () => void;
}

export const LogActivityForm: React.FC<LogActivityFormProps> = ({
    typeLabel,
    category,
    submitLabel,
    isSubmitting,
    attendeePicker,
    onSubmit,
    onCancel,
}) => {
    const [note, setNote] = React.useState("");
    const [when, setWhen] = React.useState<CalendarDateTime | null>(
        () => new CalendarDateTime(CalendarDate.todayNoTime),
    );

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit({
            note: note.trim() ? note.trim() : null,
            happenedAt: resolveHappenedAt(when),
        });
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1">
                <span className="text-sm font-medium text-gray-700">Activity type</span>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                    <ActivityCategoryIcon
                        category={category}
                        className="h-4 w-4 flex-shrink-0"
                    />
                    {typeLabel}
                </div>
            </div>
            <CalendarDateTimeInput label="When" value={when} onChange={setWhen} />
            <TextArea
                label="Note (optional)"
                value={note}
                placeholder="Add a note about this activity"
                onChange={e => setNote(e.target.value)}
            />
            {attendeePicker ? (
                <SpMultiSelect
                    label={attendeeLabel}
                    options={attendeePicker.options}
                    value={attendeePicker.value}
                    onChange={attendeePicker.onChange}
                    placeholder={attendeePlaceholder(attendeePicker.options)}
                />
            ) : null}
            <div className="flex justify-end gap-2">
                <SecondaryButton type="button" onClick={onCancel}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                    {submitLabel}
                </PrimaryButton>
            </div>
        </form>
    );
};
